import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, TrendingUp } from "lucide-react";
import { Loader, Empty, btn } from "@/components/Page";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { SKILLS, SKILL_META } from "@/lib/game";

const PERIOD_LABELS = { today: "Today", yesterday: "Yesterday", last_7d: "Last 7 Days", last_14d: "Last 14 Days" };
const PERIOD_ORDER = ["today", "yesterday", "last_7d", "last_14d"];

function gainCls(v) {
  return v > 0 ? "text-emerald-400" : v < 0 ? "text-orange-400" : "text-zinc-600";
}

function fmtGain(v) {
  return v > 0 ? `+${v}` : `${v}`;
}

export default function ArenaTrainingSummary() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    loadSummary();
  }, []);

  const loadSummary = async () => {
    try {
      // For now, we'll use a placeholder since backend doesn't have this yet
      // In production, this would call /api/arena/training/summary
      const { data: fighters } = await api.get("/arena/fighters");
      
      if (!fighters.fighters || fighters.fighters.length === 0) {
        setData(false);
        return;
      }

      // Create mock summary data structure
      const summary = {
        fighters: fighters.fighters.map(f => ({
          id: f.id,
          name: f.name,
          cls: f.cls,
          totals: {
            today: 0,
            yesterday: 0,
            last_7d: 0,
            last_14d: 0
          },
          by_skill: SKILLS.reduce((acc, skill) => ({
            ...acc,
            [skill]: {
              today: 0,
              yesterday: 0,
              last_7d: 0,
              last_14d: 0
            }
          }), {})
        })),
        grand_totals: {
          today: 0,
          yesterday: 0,
          last_7d: 0,
          last_14d: 0
        }
      };

      setData(summary);
    } catch (e) {
      toast.error(apiErr(e));
      setData(false);
    }
  };

  if (data === null) return <Loader />;
  if (!data || !data.fighters?.length) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate("/arena-circuit/training")}
            className={btn() + " bg-zinc-800 hover:bg-zinc-700"}
          >
            <ArrowLeft size={16} />
          </button>
          <h1 className="font-cinzel text-2xl text-amber-300">Training Summary</h1>
        </div>
        <Empty label="No active gladiators to summarize. Create a gladiator first." />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate("/arena-circuit/training")}
          className={btn() + " bg-zinc-800 hover:bg-zinc-700"}
        >
          <ArrowLeft size={16} />
        </button>
        <div className="flex-1">
          <h1 className="font-cinzel text-2xl text-amber-300">Training Summary</h1>
          <p className="text-sm text-zinc-400 mt-1">
            Net skill points gained per fighter. Training gains are calculated from your saved schedules.
          </p>
        </div>
      </div>

      {/* Circuit-wide Total */}
      <div className="bg-zinc-950 border border-amber-800/40 rounded-sm p-4">
        <h3 className="font-cinzel text-amber-300 uppercase tracking-wide text-sm flex items-center gap-2 mb-3">
          <TrendingUp size={15} /> Total ({data.fighters.length} fighter{data.fighters.length === 1 ? "" : "s"})
        </h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {PERIOD_ORDER.map((p) => {
            const total = data.grand_totals[p];
            return (
              <div key={p} className="bg-zinc-900/50 border border-zinc-800 rounded-sm px-3 py-2.5">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500">{PERIOD_LABELS[p]}</div>
                <div className={`font-cinzel text-2xl tabular-nums ${gainCls(total)}`}>{fmtGain(total)}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Per-fighter Breakdowns */}
      {data.fighters.map((f) => (
        <div key={f.id} className="bg-zinc-950 border border-amber-900/30 rounded-sm overflow-hidden">
          <div className="flex items-center justify-between gap-3 px-4 py-2.5 border-b border-zinc-800 bg-zinc-900/40">
            <h4 className="font-cinzel text-amber-200 uppercase tracking-wide text-sm">
              {f.name} <span className="text-zinc-600 normal-case tracking-normal text-xs capitalize">· {f.cls}</span>
            </h4>
          </div>

          {/* Period Totals */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 p-4 border-b border-zinc-800">
            {PERIOD_ORDER.map((p) => {
              const total = f.totals[p];
              return (
                <div key={p} className="bg-zinc-900/30 border border-zinc-800/50 rounded-sm px-3 py-2">
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500">{PERIOD_LABELS[p]}</div>
                  <div className={`font-cinzel text-xl tabular-nums ${gainCls(total)}`}>{fmtGain(total)}</div>
                </div>
              );
            })}
          </div>

          {/* Per-skill Breakdown */}
          <div className="p-4">
            <div className="space-y-2">
              {SKILLS.map((skill) => {
                const skillData = f.by_skill[skill];
                const Icon = SKILL_META[skill]?.icon;
                return (
                  <div key={skill} className="flex items-center gap-3">
                    <div className="flex items-center gap-2 w-28 flex-shrink-0">
                      {Icon && <Icon size={13} className="text-amber-400/70" />}
                      <span className="text-sm text-zinc-400 capitalize">{skill}</span>
                    </div>
                    <div className="flex-1 grid grid-cols-4 gap-2 text-xs text-center">
                      {PERIOD_ORDER.map((p) => (
                        <div key={p} className={`tabular-nums font-medium ${gainCls(skillData[p])}`}>
                          {fmtGain(skillData[p])}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      ))}

      {/* Placeholder Notice */}
      <div className="bg-amber-950/20 border border-amber-700/40 rounded p-4">
        <p className="text-xs text-amber-300">
          ℹ️ Training summary calculations will be implemented in a future update. Currently showing placeholder data.
        </p>
      </div>
    </div>
  );
}
